import type { User } from "@blocknote/core";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { IScriptingComment } from "@shared/lib/types/briefCampains";
import type { EditorCommentsConfig } from "@shared/ui/Editor/lib/editorComments";
import type { EditorHandle } from "@shared/ui/Editor/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toPlainQuote } from "./commentAnchor";
import { anchorsFromComments, uniqueCommentUsers } from "./commentThreadAnchors";
import { ScriptCommentThreadStore } from "./scriptCommentThreadStore";
import { useScriptConceptAutosave } from "./useScriptConceptAutosave";

const EMPTY_COMMENTS: IScriptingComment[] = [];

export function useScriptConceptComments(
  comments: IScriptingComment[] | undefined,
  scriptId: string
) {
  const { userDetails } = useGetMe();
  const userId = userDetails?.userEntity?.id || "anonymous";
  const editorRef = useRef<EditorHandle>(null);
  const [quotedText, setQuotedText] = useState<string | null>(null);
  const [pendingThreadId, setPendingThreadId] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);

  const [isEditorReady, setIsEditorReady] = useState(false);

  const autosave = useScriptConceptAutosave({
    scriptId,
    editorRef,
    onSaved: () => setSavedFlash(true),
  });

  useEffect(() => {
    if (!savedFlash) return;
    const timer = window.setTimeout(() => setSavedFlash(false), 1200);
    return () => window.clearTimeout(timer);
  }, [savedFlash]);

  const commentList = comments ?? EMPTY_COMMENTS;

  const threadStore = useMemo(() => {
    const store = new ScriptCommentThreadStore(userId);
    store.sync(comments ?? EMPTY_COMMENTS);
    return store;
  }, [userId]);

  useEffect(() => {
    threadStore.sync(commentList);
  }, [threadStore, commentList]);

  const restoreExistingMarks = useCallback(() => {
    setIsEditorReady(true);
    const anchors = anchorsFromComments(commentList, threadStore);
    editorRef.current?.restoreCommentMarks(anchors);
  }, [commentList, threadStore]);

  useEffect(() => {
    if (!isEditorReady) return;
    const anchors = anchorsFromComments(commentList, threadStore);
    editorRef.current?.restoreCommentMarks(anchors);
  }, [isEditorReady, commentList, threadStore]);

  const resolveUsers = useCallback(
    async (userIds: string[]) => {
      const currentUser: User = {
        id: userId,
        username:
          `${userDetails?.userEntity?.firstName || ""} ${
            userDetails?.userEntity?.lastName || ""
          }`.trim() || "You",
        avatarUrl: userDetails?.userEntity?.profilePicUrl || "",
      };

      const fromComments = commentList.flatMap((comment) => {
        const author = comment.createdBy;
        if (!author?._id) return [];
        return [
          {
            id: author._id,
            username:
              `${author.firstName || ""} ${author.lastName || ""}`.trim() ||
              "Unknown",
            avatarUrl: "",
          } satisfies User,
        ];
      });

      return uniqueCommentUsers([currentUser, ...fromComments]).filter((user) =>
        userIds.includes(user.id)
      );
    },
    [commentList, userDetails, userId]
  );

  const focusCommentInput = () => {
    window.setTimeout(() => {
      document.getElementById("comment-input")?.focus();
    }, 100);
  };

  const commentsConfig = useMemo<EditorCommentsConfig>(
    () => ({
      threadStore,
      resolveUsers,
      onPendingComment: (selectedText, threadId) => {
        const quote = toPlainQuote(selectedText);
        if (!quote) return;
        threadStore.ensureThread(threadId);
        setQuotedText(quote);
        setPendingThreadId(threadId);
        setSelectedThreadId(threadId);
        focusCommentInput();
      },
      onPendingQuoteChange: (selectedText, threadId) => {
        const quote = toPlainQuote(selectedText);
        setQuotedText(quote || null);
        if (!quote) {
          setPendingThreadId(null);
          return;
        }
        setPendingThreadId(threadId);
      },
      onSelectedThreadChange: (threadId) => {
        setSelectedThreadId(threadId ?? null);
      },
    }),
    [resolveUsers, threadStore]
  );

  const clearQuotedText = () => {
    setQuotedText(null);
    setPendingThreadId(null);
    editorRef.current?.stopPendingComment();
  };

  const getThreadQuote = (threadId: string) =>
    editorRef.current?.getThreadQuote(threadId) ?? null;

  const selectCommentThread = (threadId: string | null, quote?: string) => {
    setSelectedThreadId(threadId);
    if (threadId) {
      threadStore.ensureThread(threadId);
    }
    editorRef.current?.selectThread(threadId ?? undefined, quote);
  };

  const handleCommentCreated = (threadId: string, quote?: string) => {
    const liveQuote =
      editorRef.current?.getThreadQuote(threadId) ||
      quote ||
      quotedText ||
      undefined;
    threadStore.ensureThread(threadId);
    editorRef.current?.applyCommentMark(threadId, liveQuote);
    editorRef.current?.selectThread(threadId, liveQuote);
    setQuotedText(null);
    setPendingThreadId(null);
    setSelectedThreadId(threadId);
    autosave.saveImmediately();
  };

  const startCommentFromSelection = () => {
    editorRef.current?.startPendingComment();
  };

  return {
    editorRef,
    commentsConfig,
    quotedText,
    pendingThreadId,
    selectedThreadId,
    isSaving: autosave.isUpdating,
    savedFlash,
    scheduleSave: autosave.scheduleSave,
    pauseAutosaveForMedia: autosave.pauseAutosaveForMedia,
    saveAfterMediaUpload: autosave.saveAfterMediaUpload,
    resetSnapshot: autosave.resetSnapshot,
    clearQuotedText,
    selectCommentThread,
    handleCommentCreated,
    startCommentFromSelection,
    restoreExistingMarks,
    getThreadQuote,
  };
}
